/**
 * EmailTemplateEditor — supplier dispatch email template editor
 * (iter114s.76 Phase 2b).
 *
 * Lives on /admin/system. Admin can edit the global default or pick
 * a partner override, then preview which {placeholder}s are
 * supported. Persists to `db.email_templates` via the admin API.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Loader2, Save, Mail } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLACEHOLDERS = [
  { key: '{order_number}',   desc: 'EG3 order # (e.g. EG-20260512-AB46D3B2)' },
  { key: '{customer_email}', desc: 'Ordering customer email' },
  { key: '{partner_name}',   desc: 'Selected supplier display name' },
  { key: '{items_count}',    desc: 'Number of line items in the order' },
  { key: '{extra_note}',     desc: 'One-line note the admin types at dispatch time' },
];

export const EmailTemplateEditor = () => {
  const [partnerId, setPartnerId] = useState(''); // '' = global default
  const [partners, setPartners] = useState([]);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState({ updated_at: null, updated_by: null, is_default: false, is_fallback: false });

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/admin/partners`, { withCredentials: true });
        setPartners(res.data?.partners || []);
      } catch { /* ok — picker just shows default-only */ }
    })();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/admin/email-templates/supplier-dispatch`, {
          params: partnerId ? { partner_id: partnerId } : {},
          withCredentials: true,
        });
        if (cancelled) return;
        setSubject(res.data?.subject || '');
        setBody(res.data?.body || '');
        setMeta({
          updated_at: res.data?.updated_at,
          updated_by: res.data?.updated_by,
          is_default: !!res.data?.is_default,
          is_fallback: !!res.data?.is_fallback,
        });
      } catch (e) {
        toast.error('Failed to load template.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [partnerId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API_URL}/api/admin/email-templates/supplier-dispatch`, {
        partner_id: partnerId || null,
        subject,
        body,
      }, { withCredentials: true });
      toast.success(partnerId ? 'Per-partner template saved.' : 'Default template saved.');
      setMeta({ updated_at: res.data?.updated_at, updated_by: res.data?.updated_by, is_default: false, is_fallback: false });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const insertPlaceholder = (token) => {
    setBody((prev) => `${prev}${token}`);
  };

  return (
    <div
      className="bg-white rounded-lg shadow-sm border border-[#E5E7EB] p-4 space-y-3"
      data-testid="email-template-editor"
    >
      <div className="flex items-center gap-2">
        <Mail className="w-4 h-4 text-[#2F5D3A]" />
        <h2 className="text-sm font-bold text-[#14281D]">Supplier Dispatch Email Template</h2>
      </div>
      <p className="text-xs text-[#1F3D2C]/65">
        Composed every time you click "Send to Supplier" from the Action Queue. Save a per-partner override below to customise a single vendor's email without changing the default.
      </p>

      <div className="flex items-center gap-2">
        <label className="text-xs font-semibold text-[#14281D]">Edit template for:</label>
        <select
          value={partnerId}
          onChange={(e) => setPartnerId(e.target.value)}
          className="text-sm border border-[#6FBF73]/30 rounded px-2 py-1 bg-white"
          data-testid="email-template-partner-select"
        >
          <option value="">Global default (all partners)</option>
          {partners.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
        {meta.is_fallback && (
          <span className="text-[10px] text-orange-600 font-semibold">
            (No override saved — editing the global default)
          </span>
        )}
        {meta.is_default && (
          <span className="text-[10px] text-gray-500">(Built-in default — save to persist)</span>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-xs text-gray-500 py-6">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading template…
        </div>
      ) : (
        <>
          <div>
            <label className="block text-xs font-semibold text-[#14281D] mb-1">Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full text-sm border border-[#6FBF73]/30 rounded px-3 py-2"
              data-testid="email-template-subject"
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs font-semibold text-[#14281D]">Body</label>
              <div className="flex flex-wrap gap-1.5">
                {PLACEHOLDERS.map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => insertPlaceholder(p.key)}
                    title={p.desc}
                    className="text-[10px] font-mono bg-[#E8F1E6] hover:bg-[#D7E8D9] text-[#2F5D3A] px-1.5 py-0.5 rounded transition"
                    data-testid={`email-template-placeholder-${p.key.replace(/[{}]/g, '')}`}
                  >
                    {p.key}
                  </button>
                ))}
              </div>
            </div>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={10}
              className="w-full text-sm border border-[#6FBF73]/30 rounded px-3 py-2 font-mono leading-snug"
              data-testid="email-template-body"
            />
          </div>
          <div className="flex items-center justify-between">
            <p className="text-[10px] text-gray-500">
              {meta.updated_at
                ? `Last saved ${new Date(meta.updated_at).toLocaleString()} by ${meta.updated_by || 'unknown'}.`
                : 'Never saved — using built-in default.'}
            </p>
            <Button
              onClick={handleSave}
              disabled={saving || !subject || !body}
              className="bg-[#4A7C59] hover:bg-[#3D6A4A] text-white text-xs rounded-full px-4"
              data-testid="email-template-save"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}
              Save template
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default EmailTemplateEditor;
